/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Table component for displaying bike bus list with management actions.
Handles viewing, editing, and deleting bike buses with passenger information.
*/

import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Eye, Edit, Trash2, Users, MapPin, Clock, MoreHorizontal, CheckCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { bikeBusService, BikeBus, BikeBusDetails, BikeBusStatus } from "@/api/bikeBusService";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { BikeBusEditDialog } from "./BikeBusEditDialog";
import { BikeBusTimeline } from "./BikeBusTimeline";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookingDetailsDialog } from "@/components/BookingDetailsDialog";
import { supabase } from "@/integrations/supabase/client";

interface BikeBusTableProps {
  resetToFirstPage?: boolean;
}

export const BikeBusTable = ({ resetToFirstPage }: BikeBusTableProps = {}) => {
  const [page, setPage] = useState(1);
  const [selectedBikeBus, setSelectedBikeBus] = useState<BikeBusDetails | null>(null);
  const [editingBikeBus, setEditingBikeBus] = useState<BikeBus | null>(null);
  const [deletingBikeBus, setDeletingBikeBus] = useState<BikeBus | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const pageSize = 10;

  // Reset to page 1 when resetToFirstPage prop is true
  useEffect(() => {
    if (resetToFirstPage) {
      setPage(1);
    }
  }, [resetToFirstPage]);

  // Get bike buses list
  const { data: bikeBusData, isLoading } = useQuery({
    queryKey: ["bike-buses", page, user?.id],
    queryFn: () => bikeBusService.getBikeBuses(page, pageSize, user?.id)
  });

  const bikeBuses = bikeBusData?.bikeBuses || [];
  const totalPages = Math.ceil((bikeBusData?.total || 0) / pageSize);

  // Format stop time and convert from UTC to London time for display
  const formatStopTime = (timeString: string, bikeBusDate: string) => {
    try {
      // Convert stored UTC time back to London time for display
      if (timeString.includes('T')) {
        let utcDate: Date;
        
        // Handle different timestamp formats
        if (timeString.includes('+') || timeString.endsWith('Z')) {
          // Already has timezone info (e.g., "2025-07-31T07:00:00+00:00" or "2025-07-31T07:00:00Z")
          utcDate = new Date(timeString);
        } else {
          // No timezone info, assume UTC (e.g., "2025-07-31T07:00:00")
          const utcDateTime = `${timeString}Z`; // Add Z to indicate UTC
          utcDate = new Date(utcDateTime);
        }
        
        // Validate UTC date parsing
        if (isNaN(utcDate.getTime())) {
          return timeString; // Fallback to original
        }
        
        // Format as London time
        const londonTime = utcDate.toLocaleTimeString('en-GB', {
          timeZone: 'Europe/London',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        
        // Validate formatting result
        if (!londonTime || londonTime === 'Invalid Date') {
          return timeString; // Fallback to original
        }
        
        return londonTime;
      }
      // If it's already just time (HH:MM), assume it's already in London time
      return timeString;
    } catch (error) {
      // Fallback for any parsing errors
      return timeString;
    }
  };

  const handleView = async (bikeBus: BikeBus) => {
    try {
      const details = await bikeBusService.getBikeBus(bikeBus.bikeBusId);
      setSelectedBikeBus(details);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load bike bus details");
    }
  };

  const handleEdit = (bikeBus: BikeBus) => {
    setEditingBikeBus(bikeBus);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (bikeBus: BikeBus) => {
    setDeletingBikeBus(bikeBus);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingBikeBus) return;
    
    try {
      await bikeBusService.cancelBikeBus(deletingBikeBus.bikeBusId);
      queryClient.invalidateQueries({ queryKey: ["bike-buses"] });
      setIsDeleteDialogOpen(false);
      setDeletingBikeBus(null);
    } catch (error) {
      toast.error("Failed to cancel bike bus");
    }
  };

  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["bike-buses"] });
    setIsEditDialogOpen(false);
    setEditingBikeBus(null);
  };

  const handleViewBooking = async (bookingId: string) => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          user:users(username),
          bike:bikes(serial_number),
          start_site:sites!bookings_start_location_id_fkey(name),
          end_site:sites!bookings_end_location_id_fkey(name)
        `)
        .eq('id', bookingId)
        .single();

      if (error) throw error;
      
      if (data) {
        setSelectedBooking(data);
        setIsBookingDialogOpen(true);
      }
    } catch (error) {
      console.error("Error fetching booking:", error);
      toast.error("Failed to load booking details");
    }
  };

  const getStatusBadgeVariant = (status: BikeBusStatus) => {
    switch (status) {
      case "pending":
        return "default";
      case "completed":
        return "secondary";
      case "cancelled":
        return "destructive";
      default:
        return "outline";
    }
  };

  const formatStopsPreview = (stops: BikeBus['stops']) => {
    if (stops.length === 0) return "No stops";
    if (stops.length === 1) return stops[0].stop;
    return `${stops[0].stop} +${stops.length - 1} more`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Group Leader</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Stops</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Your Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bikeBuses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No bike buses found
                </TableCell>
              </TableRow>
            ) : (
                           bikeBuses.map((bikeBus, index) => (
               <TableRow key={`bikeBus-${bikeBus.bikeBusId || index}`}>
                  <TableCell>
                    {format(new Date(bikeBus.date), "MMM dd, yyyy")}
                  </TableCell>
                  <TableCell className="font-medium">
                    {bikeBus.leader}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(bikeBus.status)}>
                      {bikeBus.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <MapPin className="h-3 w-3" />
                      {formatStopsPreview(bikeBus.stops)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Users className="h-3 w-3" />
                      {bikeBus.memberCount}
                    </div>
                  </TableCell>
                  <TableCell>
                    {bikeBus.isCurrentUserMember ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm text-green-600">
                          <CheckCircle className="h-3 w-3" />
                          <span>Joined</span>
                          {bikeBus.currentUserMembership?.status && (
                            <Badge variant="outline" className="text-xs">
                              {bikeBus.currentUserMembership.status}
                            </Badge>
                          )}
                        </div>
                        {(bikeBus.currentUserMembership?.startStopId || bikeBus.currentUserMembership?.endStopId) && (
                          <div className="text-xs text-muted-foreground">
                            {(() => {
                              const startStop = bikeBus.stops.find(stop => stop.id === bikeBus.currentUserMembership?.startStopId);
                              const endStop = bikeBus.stops.find(stop => stop.id === bikeBus.currentUserMembership?.endStopId);
                              if (startStop && endStop && startStop.id !== endStop.id) {
                                return `${startStop.siteName || startStop.stop} → ${endStop.siteName || endStop.stop}`;
                              } else if (startStop) {
                                return `From ${startStop.siteName || startStop.stop}`;
                              } else if (endStop) {
                                return `To ${endStop.siteName || endStop.stop}`;
                              }
                              return "Full route";
                            })()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Not joined</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleView(bikeBus)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(bikeBus)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(bikeBus)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Cancel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* View Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bike Bus Details</DialogTitle>
          </DialogHeader>
          {selectedBikeBus && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium">Group Leader</h4>
                  <p className="text-sm text-muted-foreground">{selectedBikeBus.leader}</p>
                </div>
                <div>
                  <h4 className="font-medium">Date</h4>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(selectedBikeBus.date), "MMMM dd, yyyy")}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium">Status</h4>
                  <Badge variant={getStatusBadgeVariant(selectedBikeBus.status)}>
                    {selectedBikeBus.status}
                  </Badge>
                </div>
                <div>
                  <h4 className="font-medium">Members</h4>
                  <p className="text-sm text-muted-foreground">{selectedBikeBus.members.length}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Stops & Timeline</h4>
                <Tabs defaultValue="timeline" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="timeline">Timeline View</TabsTrigger>
                    <TabsTrigger value="list">List View</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="timeline" className="mt-4">
                    <BikeBusTimeline 
                      stops={selectedBikeBus.stops}
                      members={selectedBikeBus.members}
                      date={selectedBikeBus.date}
                    />
                  </TabsContent>
                  
                  <TabsContent value="list" className="mt-4">
                    <div className="space-y-2">
                      {selectedBikeBus.stops.map((stop, index) => {
                        return (
                          <Card key={`stop-${stop.bikeBusId}-${stop.siteId}-${stop.index || index}`}>
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">Stop {stop.index || (index + 1)}:</span>
                                  <span className="text-sm">{stop.stop}</span>
                                  <span className="text-xs text-muted-foreground">({stop.siteName})</span>
                                </div>
                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <Clock className="h-3 w-3" />
                                  {formatStopTime(stop.time, selectedBikeBus.date)}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {selectedBikeBus.members.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Members ({selectedBikeBus.members.length})</h4>
                  <div className="space-y-2">
                    {selectedBikeBus.members.map((member, index) => {
                      // Find start and end stop details
                      const startStop = selectedBikeBus.stops.find(stop => stop.id === member.startStopId);
                      const endStop = selectedBikeBus.stops.find(stop => stop.id === member.endStopId);
                      
                      return (
                        <Card key={`member-${member.bikeBusId}-${member.userId}-${index}`}>
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-sm">{member.userName}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline" className="text-xs">
                                      {member.status}
                                    </Badge>
                                                                         {member.bookingId && (
                                       <Button
                                         variant="outline"
                                         size="sm"
                                         className="h-6 px-2 text-xs"
                                         onClick={() => handleViewBooking(member.bookingId)}
                                       >
                                         View Booking
                                       </Button>
                                     )}
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Joined: {format(member.createdAt, "MMM dd, HH:mm")}
                                </div>
                              </div>
                              
                                                             {(startStop || endStop) && (
                                 <div className="flex items-center gap-4 text-sm">
                                   {startStop && (
                                     <div className="flex items-center gap-1">
                                       <span className="text-green-600 font-medium">From:</span>
                                       <span>{startStop.siteName || startStop.stop}</span>
                                       <span className="text-xs text-muted-foreground">
                                         ({formatStopTime(startStop.time, selectedBikeBus.date)})
                                       </span>
                                     </div>
                                   )}
                                   {endStop && (
                                     <div className="flex items-center gap-1">
                                       <span className="text-red-600 font-medium">To:</span>
                                       <span>{endStop.siteName || endStop.stop}</span>
                                       <span className="text-xs text-muted-foreground">
                                         ({formatStopTime(endStop.time, selectedBikeBus.date)})
                                       </span>
                                     </div>
                                   )}
                                   {!startStop && !endStop && (
                                     <span className="text-xs text-muted-foreground">Full route</span>
                                   )}
                                 </div>
                               )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      {editingBikeBus && (
        <BikeBusEditDialog
          bikeBus={editingBikeBus}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        title="Cancel Bike Bus"
        description={`Are you sure you want to cancel this bike bus? This action will cancel the bike bus and notify all ${deletingBikeBus?.memberCount || 0} members.`}
      />

      {/* Booking Details Dialog */}
      <BookingDetailsDialog
        isOpen={isBookingDialogOpen}
        onOpenChange={setIsBookingDialogOpen}
        booking={selectedBooking}
      />
    </>
  );
}; 